/**
 * Settings > Gates (SAM-946, mock Gates v3): the tenant's external and internal gates. Tenant admin and Xhinola only
 * (the route is requireAdmin). Tailwind + tokens (styling freeze, SAM-175).
 */
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Landmark, Loader2, Plus, Search } from 'lucide-react';
import { useGates, useGateTypes } from '../../../hooks/useGates';
import { Notice } from '../../../components/ui/Notice';
import { GATE_ZONE_ENABLED, filterGates, gateCounts, toGateRows, type GateRow, type GateTab } from './gatesModel';

const TABS: Array<{ id: GateTab; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'external', label: 'External' },
  { id: 'internal', label: 'Internal' },
];

const PILL: Record<string, string> = {
  external: 'bg-[#E7F0EE] text-[#0B5C4D]',
  internal: 'bg-[#EEF2FF] text-[#3730A3]',
};

const TypePill: React.FC<{ row: GateRow }> = ({ row }) => (
  <span className={`inline-flex shrink-0 whitespace-nowrap rounded-full px-[10px] py-[3px] text-xs font-semibold ${(row.kind && PILL[row.kind]) || 'bg-[var(--line-subtle)] text-[var(--ink-700)]'}`}>
    {row.typeName || '—'}
  </span>
);

const NewGateLink: React.FC = () => (
  <Link
    to="/settings/gates/new"
    className="inline-flex h-11 items-center gap-1.5 rounded-lg bg-[var(--tenant-primary)] px-4 text-sm font-semibold text-white no-underline"
  >
    <Plus size={16} aria-hidden /> New gate
  </Link>
);

export const GatesPage: React.FC = () => {
  const { data: gates = [], isLoading, isError, refetch } = useGates();
  const { data: types = [] } = useGateTypes();
  const [tab, setTab] = useState<GateTab>('all');
  const [search, setSearch] = useState('');

  const rows = useMemo(() => toGateRows(gates, types), [gates, types]);
  const counts = gateCounts(rows);
  const shown = filterGates(rows, tab, search);

  return (
    <div className="h-full overflow-auto bg-[var(--surface)]">
      <main className="flex min-w-0 flex-col gap-5 px-4 pb-10 pt-4 md:px-8 md:pb-12 md:pt-7">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-[13px] text-[var(--ink-500)]">Settings</span>
            <h1 className="m-0 text-[26px] font-bold">Gates</h1>
            {!isLoading && !isError && (
              <span className="text-sm text-[var(--ink-500)]">
                {rows.length === 0 ? 'External and internal gates of the community' : `${counts.external} external · ${counts.internal} internal`}
              </span>
            )}
          </div>
          {rows.length > 0 && <NewGateLink />}
        </div>

        {isLoading ? (
          <div role="status" aria-busy="true" className="flex flex-col items-center gap-2 py-16 text-sm text-[var(--ink-500)]">
            <Loader2 size={18} className="animate-spin" aria-hidden />
            Loading gates…
          </div>
        ) : isError ? (
          <Notice variant="error">
            Could not load the gates.{' '}
            <button type="button" className="btn" onClick={() => refetch()}>
              Try again
            </button>
          </Notice>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg bg-white px-6 py-14 text-center shadow-[var(--sh-1)]">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#E7F0EE] text-[var(--tenant-primary)]">
              <Landmark size={28} aria-hidden />
            </span>
            <h2 className="m-0 text-lg font-bold">No gates yet</h2>
            <p className="m-0 max-w-[380px] text-sm leading-normal text-[var(--ink-700)]">
              Add the community&apos;s external and internal gates. Invitation Types use them in their gate rules.
            </p>
            <NewGateLink />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div role="tablist" aria-label="Gate type" className="flex gap-6 border-b border-solid border-[var(--line)]">
                {TABS.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={tab === t.id}
                    onClick={() => setTab(t.id)}
                    className={`-mb-px border-0 border-b-2 border-solid bg-transparent px-0.5 py-2.5 text-sm ${
                      tab === t.id
                        ? 'border-b-[var(--tenant-primary)] font-semibold text-[var(--tenant-primary)]'
                        : 'border-b-transparent font-normal text-[var(--ink-500)]'
                    }`}
                  >
                    {t.label} {counts[t.id]}
                  </button>
                ))}
              </div>
              <label className="flex h-11 w-full items-center gap-2 rounded-lg border border-solid border-[var(--line)] bg-white px-3 text-[var(--ink-500)] md:w-[300px]">
                <Search size={16} aria-hidden />
                <input
                  aria-label="Search by name or code"
                  placeholder="Search by name or code"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="min-w-0 grow border-0 bg-transparent text-sm outline-none"
                />
              </label>
            </div>

            {shown.length === 0 ? (
              <div role="status" className="rounded-lg bg-white px-6 py-10 text-center text-sm text-[var(--ink-500)] shadow-[var(--sh-1)]">
                No gates match.
              </div>
            ) : (
              <>
                <div className="hidden overflow-hidden rounded-lg bg-white shadow-[var(--sh-1)] md:block">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-[var(--surface)] text-left text-xs uppercase tracking-[0.04em] text-[var(--ink-700)]">
                        <th className="px-5 py-3 font-semibold">Name</th>
                        <th className="px-4 py-3 font-semibold">Code</th>
                        <th className="px-4 py-3 font-semibold">Type</th>
                        {GATE_ZONE_ENABLED && <th className="px-4 py-3 font-semibold">Zone</th>}
                        <th className="px-5 py-3 text-right font-semibold"><span className="sr-only">Edit</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {shown.map((r) => (
                        <tr key={r.id} className="border-t border-solid border-[var(--line-subtle)]">
                          <td className="px-5 py-3.5 font-semibold">{r.name}</td>
                          <td className="px-4 py-3.5 font-mono text-[13px] text-[var(--ink-700)]">{r.code}</td>
                          <td className="px-4 py-3.5"><TypePill row={r} /></td>
                          {GATE_ZONE_ENABLED && <td className="px-4 py-3.5 text-[var(--ink-400)]">—</td>}
                          <td className="px-5 py-3.5 text-right">
                            <Link
                              to={`/settings/gates/${r.id}`}
                              aria-label={`Edit ${r.name}`}
                              className="inline-flex h-9 items-center rounded-lg border border-solid border-[var(--line)] px-3.5 text-[13px] font-semibold text-[var(--ink-700)] no-underline"
                            >
                              Edit
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <ul className="m-0 flex list-none flex-col gap-2.5 p-0 md:hidden">
                  {shown.map((r) => (
                    <li key={r.id}>
                      <Link
                        to={`/settings/gates/${r.id}`}
                        className="flex items-center justify-between gap-3 rounded-lg bg-white px-4 py-3.5 text-[var(--ink-900)] no-underline shadow-[var(--sh-1)]"
                      >
                        <span className="flex min-w-0 flex-col gap-[3px]">
                          <span className="text-[15px] font-semibold">{r.name}</span>
                          <span className="font-mono text-[13px] text-[var(--ink-500)]">{r.code}</span>
                        </span>
                        <TypePill row={r} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
};
